import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { AlertCircleIcon, ArrowLeftIcon, ImageIcon, Loader2Icon, XIcon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { SelectField, TextArea, TextField } from '../../components/ui/Field';
import { useAdminData } from '../../contexts/AdminDataContext';
import { validateImage } from '../../services/imageService';
import { caseCategories, caseStatuses, priceModels } from '../../types/admin';
import type { CaseCategory, CaseStatus, PriceModel } from '../../types/admin';
const NEW_CUSTOMER = 'ny';
interface Selected {
  file: File;
  preview: string;
}
export function CaseNew() {
  const {
    customers,
    createCase,
    settings
  } = useAdminData();
  const navigate = useNavigate();
  const [customerId, setCustomerId] = useState(customers[0]?.id ?? NEW_CUSTOMER);
  const [name, setName] = useState('');
  const [title, setTitle] = useState('');
  const [address, setAddress] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState<CaseCategory>('Multiservice');
  const [priceModel, setPriceModel] = useState<PriceModel>('Tilbud endnu ikke lavet');
  const [hourlyRate, setHourlyRate] = useState(settings.hourlyRate);
  const [fixedPrice, setFixedPrice] = useState('');
  const [plannedDate, setPlannedDate] = useState('');
  const [status, setStatus] = useState<CaseStatus>('Ny henvendelse');
  const [internalNote, setInternalNote] = useState('');
  const [photos, setPhotos] = useState<Selected[]>([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const creatingCustomer = customerId === NEW_CUSTOMER || customers.length === 0;
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-463" data-mp="8byl4j6w2ffj">
      <Link to="/admin/sager" className="inline-flex items-center gap-2 text-sm text-ink-muted transition-colors duration-200 ease-smooth hover:text-ink" data-id="element-464" data-mp="8eba3gb2dq23">
        <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-465" data-mp="2f21vddf13vd2" />
        Sager
      </Link>
      <h1 className="mt-4 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-466" data-mp="2a8z3d9pwgtyz">
        Opret sag
      </h1>

      <form className="mt-7 max-w-3xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      setError('');
      const created = await createCase({
        customerId: creatingCustomer ? '' : customerId,
        newCustomer: creatingCustomer ? {
          name,
          phone,
          email,
          address
        } : undefined,
        title,
        address,
        description,
        category,
        priceModel,
        hourlyRate: priceModel === 'Timepris' ? hourlyRate : '',
        fixedPrice: priceModel === 'Fast pris' ? fixedPrice : '',
        plannedDate,
        status,
        internalNote,
        photos: photos.map(photo => ({
          url: photo.preview,
          name: photo.file.name,
          file: photo.file
        }))
      });
      setSaving(false);
      if (!created) {
        setError('Sagen kunne ikke gemmes. Tjek din forbindelse, og prøv igen.');
        return;
      }
      navigate(`/admin/sager/${created.id}`);
    }} data-id="element-467" data-mp="219c9d8zi2qfy">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-468" data-mp="1vltt4pycsep">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-469" data-mp="6slz3dez9r4r">Kunde</h2>
          <SelectField id="ns-kunde" label="Vælg kunde" className="mt-5" value={customerId} onChange={event => setCustomerId(event.target.value)} options={[...customers.map(customer => ({
          value: customer.id,
          label: customer.name
        })), {
          value: NEW_CUSTOMER,
          label: '+ Opret ny kunde'
        }]} data-id="element-470" data-mp="bqdp21w2b36b" />
          {creatingCustomer && <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-471" data-mp="1r9jrb9rogb0s">
              <TextField id="ns-navn" label="Navn" required value={name} onChange={event => setName(event.target.value)} data-id="element-472" data-mp="1r80czopl0a1n" />
              <TextField id="ns-tlf" label="Telefonnummer" type="tel" value={phone} onChange={event => setPhone(event.target.value)} data-id="element-473" data-mp="215n0v0ouzbxr" />
              <TextField id="ns-mail" label="E-mail" type="email" className="sm:col-span-2" value={email} onChange={event => setEmail(event.target.value)} data-id="element-474" data-mp="13l0huqww5s74" />
            </div>}
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-475" data-mp="prss74sdf6s2">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-476" data-mp="ku9eq73hyy16">Opgave</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-477" data-mp="2whcunb4krepa">
            <TextField id="ns-titel" label="Opgavetitel" required placeholder="Fx montering af hegn" value={title} onChange={event => setTitle(event.target.value)} className="sm:col-span-2" data-id="element-478" data-mp="2wfrxreusfjo9" />
            <TextField id="ns-adresse" label="Adresse" placeholder="Vej, nummer, postnr. og by" value={address} onChange={event => setAddress(event.target.value)} className="sm:col-span-2" data-id="element-479" data-mp="2rfxadla81c7y" />
            <SelectField id="ns-kategori" label="Kategori" value={category} onChange={event => setCategory(event.target.value as CaseCategory)} options={caseCategories.map(option => ({
            value: option,
            label: option
          }))} data-id="element-480" data-mp="366moavf2h5fi" />
            <TextField id="ns-dato" label="Planlagt dato" type="date" value={plannedDate} onChange={event => setPlannedDate(event.target.value)} data-id="element-481" data-mp="1zor4spbqemba" />
          </div>
          <TextArea id="ns-beskrivelse" label="Opgavebeskrivelse" className="mt-5" rows={4} value={description} onChange={event => setDescription(event.target.value)} data-id="element-482" data-mp="3rponmhr9zgbh" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-483" data-mp="3dqf4b97rw63a">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-484" data-mp="3dp41bbm5j2d8">Pris og status</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-485" data-mp="38rlym2yq9jdo">
            <SelectField id="ns-prisform" label="Prisform" value={priceModel} onChange={event => setPriceModel(event.target.value as PriceModel)} options={priceModels.map(option => ({
            value: option,
            label: option
          }))} data-id="element-486" data-mp="34qiirggojncg" />
            <SelectField id="ns-status" label="Status" value={status} onChange={event => setStatus(event.target.value as CaseStatus)} options={caseStatuses.map(option => ({
            value: option,
            label: option
          }))} data-id="element-487" data-mp="2kuxvc8futnnu" />
            {priceModel === 'Timepris' && <TextField id="ns-timepris" label="Timepris (kr.)" inputMode="numeric" value={hourlyRate} onChange={event => setHourlyRate(event.target.value)} data-id="element-488" data-mp="2ypi5e01yb0t9" />}
            {priceModel === 'Fast pris' && <TextField id="ns-fastpris" label="Fast pris (kr.)" inputMode="numeric" value={fixedPrice} onChange={event => setFixedPrice(event.target.value)} data-id="element-489" data-mp="2fwwbvmeinmbu" />}
          </div>
          <TextArea id="ns-noter" label="Interne noter" className="mt-5" rows={3} placeholder="Fx: kunden ønsker arbejdet udført efter kl. 15." value={internalNote} onChange={event => setInternalNote(event.target.value)} data-id="element-490" data-mp="3rdw1ubde5p2p" />
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-491" data-mp="370ktmq7pt9tz">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-492" data-mp="3lq0drncqqh0p">Billeder</h2>
          <label htmlFor="ns-billeder" className="mt-5 flex cursor-pointer flex-col items-center justify-center gap-1.5 rounded-xl border border-dashed border-line bg-canvas px-6 py-7 text-center transition-colors duration-200 ease-smooth hover:border-accent/60" data-id="element-493" data-mp="3gqw6x3dagvdj">
            <ImageIcon className="h-5 w-5 text-ink-muted" strokeWidth={1.5} aria-hidden="true" data-id="element-494" data-mp="20cnwg75smci9" />
            <span className="text-[15px] font-medium text-ink" data-id="element-495" data-mp="1wb2azxf70pwg">Vælg billeder</span>
            <span className="text-sm text-ink-muted" data-id="element-496" data-mp="1wd5cpeppk7h0">Op til 10 MB pr. billede</span>
            <input id="ns-billeder" type="file" multiple accept="image/*" className="sr-only" onChange={event => {
            const input = event.target;
            const accepted: Selected[] = [];
            const problems: string[] = [];
            Array.from(input.files ?? []).forEach(file => {
              const problem = validateImage(file);
              if (problem) problems.push(problem);else accepted.push({
                file,
                preview: URL.createObjectURL(file)
              });
            });
            setPhotos(current => [...current, ...accepted]);
            setError(problems.join(' '));
            input.value = '';
          }} data-id="element-497" data-mp="1m7nhw5s3djq7" />
          </label>
          {photos.length > 0 && <ul className="mt-4 grid grid-cols-3 gap-3 sm:grid-cols-4" data-id="element-498" data-mp="2svaoaz15a5v6">
              {photos.map((photo, index) => <li key={`${photo.file.name}-${index}`} className="relative" data-id="element-499" data-mp="dvq6t09hkld5" data-mpi={index}>
                  <img src={photo.preview} alt={photo.file.name} className="aspect-square w-full rounded-xl object-cover" data-id="element-500" data-mp="imf0mm8n1ufg" />
                  <button type="button" onClick={() => setPhotos(current => {
              URL.revokeObjectURL(current[index].preview);
              return current.filter((_, i) => i !== index);
            })} aria-label={`Fjern billede ${photo.file.name}`} className="absolute right-2 top-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-white/90 text-ink shadow-card transition-colors duration-200 ease-smooth hover:bg-white" data-id="element-501" data-mp="8wlyebudyrnv">
                    <XIcon className="h-4 w-4" aria-hidden="true" data-id="element-502" data-mp="3dc4zyoiy62dx" />
                  </button>
                </li>)}
            </ul>}
        </section>

        {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-503" data-mp="30t9g2tlzz1jr">
            <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-504" data-mp="3essjxa7rzqq9" />
            {error}
          </p>}

        <div className="flex flex-col gap-3 sm:flex-row" data-id="element-505" data-mp="39uha2zhhdg81">
          <Button type="submit" size="lg" disabled={saving} data-id="element-506" data-mp="2m3btgi3pujwo">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-507" data-mp="2h50ke9l8wdfi" />
                Gemmer …
              </> : 'Gem sag'}
          </Button>
          <Button type="button" variant="secondary" size="lg" onClick={() => navigate('/admin/sager')} data-id="element-508" data-mp="2r26d1m9icchu">
            Annuller
          </Button>
        </div>
      </form>
    </div>;
}